import { supabase, handleSupabaseError } from '@/lib/supabase';

/**
 * Layanan PIN transaksi nasabah.
 *
 * PIN diverifikasi & di-hash SEPENUHNYA di server (RPC `change_pin`, bcrypt via
 * pgcrypto). Klien tidak pernah menerima atau membandingkan hash PIN.
 */
export type PinVerifyStatus = 'ok' | 'wrong' | 'locked' | 'no_pin';

export interface PinVerifyResult {
  status: PinVerifyStatus;
  attemptsLeft?: number;
  lockedUntil?: Date;
}

class PinService {
  /**
   * Atur/ubah PIN nasabah saat ini.
   * @param oldPin PIN lama (null bila PIN belum pernah diatur).
   * @param newPin PIN baru (6 digit angka).
   */
  async changePin(oldPin: string | null, newPin: string): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase.rpc('change_pin', {
        p_old_pin: oldPin,
        p_new_pin: newPin,
      });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: handleSupabaseError(error) };
    }
  }

  /**
   * Verifikasi PIN. Mengembalikan status terstruktur (termasuk lockout).
   */
  async verifyPin(pin: string): Promise<{ data: PinVerifyResult | null; error: string | null }> {
    try {
      const { data, error } = await supabase.rpc('verify_pin', { p_pin: pin });
      if (error) throw error;
      const raw = data as { status: PinVerifyStatus; attempts_left?: number; locked_until?: string };
      return {
        data: {
          status: raw.status,
          attemptsLeft: raw.attempts_left,
          lockedUntil: raw.locked_until ? new Date(raw.locked_until) : undefined,
        },
        error: null,
      };
    } catch (error) {
      return { data: null, error: handleSupabaseError(error) };
    }
  }

  /** Bersihkan lockout PIN (dipakai setelah verifikasi password pada alur Lupa PIN). */
  async resetPinLock(): Promise<{ error: string | null }> {
    try {
      const { error } = await supabase.rpc('reset_pin_lock');
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: handleSupabaseError(error) };
    }
  }
}

export const pinService = new PinService();
export default pinService;
